import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface NavItem {
  path: string;
  labelKey: string;
}

const enNav: NavItem[] = [
  { path: '/', labelKey: 'nav.main' },
  { path: '/en/search', labelKey: 'nav.search' },
  { path: '/en/about', labelKey: 'nav.about' },
  { path: '/en/experience', labelKey: 'nav.experience' },
  { path: '/en/projects', labelKey: 'nav.projects' },
  { path: '/en/technology', labelKey: 'nav.technology' },
  { path: '/en/interest', labelKey: 'nav.interest' },
  { path: '/en/hobby', labelKey: 'nav.hobby' },
];

const ruNav: NavItem[] = [
  { path: '/ru', labelKey: 'nav.main' },
  { path: '/ru/search', labelKey: 'nav.search' },
  { path: '/ru/about', labelKey: 'nav.about' },
  { path: '/ru/experience', labelKey: 'nav.experience' },
  { path: '/ru/projects', labelKey: 'nav.projects' },
  { path: '/ru/technology', labelKey: 'nav.technology' },
  { path: '/ru/interest', labelKey: 'nav.interest' },
  { path: '/ru/hobby', labelKey: 'nav.hobby' },
];

const itNav: NavItem[] = [
  { path: '/it', labelKey: 'nav.main' },
  { path: '/it/search', labelKey: 'nav.search' },
  { path: '/it/about', labelKey: 'nav.about' },
  { path: '/it/experience', labelKey: 'nav.experience' },
  { path: '/it/projects', labelKey: 'nav.projects' },
  { path: '/it/technology', labelKey: 'nav.technology' },
  { path: '/it/interest', labelKey: 'nav.interest' },
  { path: '/it/hobby', labelKey: 'nav.hobby' },
];

const frNav: NavItem[] = [
  { path: '/fr', labelKey: 'nav.main' },
  { path: '/fr/search', labelKey: 'nav.search' },
  { path: '/fr/about', labelKey: 'nav.about' },
  { path: '/fr/experience', labelKey: 'nav.experience' },
  { path: '/fr/projects', labelKey: 'nav.projects' },
  { path: '/fr/technology', labelKey: 'nav.technology' },
  { path: '/fr/interest', labelKey: 'nav.interest' },
  { path: '/fr/hobby', labelKey: 'nav.hobby' },
];

const navMap: Record<string, NavItem[]> = { en: enNav, ru: ruNav, it: itNav, fr: frNav };

function getLangTogglePaths(pathname: string): Record<string, string> {
  if (pathname === '/' || pathname === '/ru' || pathname === '/it' || pathname === '/fr') {
    return { en: '/', ru: '/ru', it: '/it', fr: '/fr' };
  }
  const match = pathname.match(/^\/(en|ru|it|fr)\/(.+)$/);
  if (match) {
    const section = match[2];
    return { en: `/en/${section}`, ru: `/ru/${section}`, it: `/it/${section}`, fr: `/fr/${section}` };
  }
  return { en: '/', ru: '/ru', it: '/it', fr: '/fr' };
}

interface LayoutProps {
  lang: 'en' | 'ru' | 'it' | 'fr';
  children: React.ReactNode;
}

export default function Layout({ lang, children }: LayoutProps) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation('common');
  const nav = navMap[lang] ?? enNav;
  const paths = getLangTogglePaths(pathname);

  useEffect(() => {
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return (
    <>
      <div className="container">
        <header className="blog-header py-3">
          <div className="row">
            <div className="col-8">
              <nav className="navbar navbar-expand-lg navbar-light bg-light">
                <button
                  className="navbar-toggler"
                  type="button"
                  data-toggle="collapse"
                  data-target="#navbarSupportedContent"
                  aria-controls="navbarSupportedContent"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                >
                  <span className="navbar-toggler-icon"></span>
                </button>
                <div className="collapse navbar-collapse" id="navbarSupportedContent">
                  <ul className="navbar-nav mr-auto">
                    {nav.map(({ path, labelKey }) => (
                      <li key={path} className={`nav-item${pathname === path ? ' active' : ''}`}>
                        <Link className="nav-link" to={path}>
                          {t(labelKey)}
                          {pathname === path && <span className="sr-only"> (current)</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </nav>
            </div>
            <div className="col-4 d-flex justify-content-end align-items-center" style={{ gap: '6px' }}>
              <Link className="text-muted" to={paths.ru}>
                <img width="32" src="/image/russia_square_icon_64.png" alt="RU" />
              </Link>
              <Link className="text-muted" to={paths.en}>
                <img width="32" src="/image/united_kingdom_square_icon_64.png" alt="EN" />
              </Link>
              <Link className="text-muted" to={paths.it}>
                <img width="32" height="24" src="/image/italy_square_icon.svg" alt="IT" style={{ display: 'block' }} />
              </Link>
              <Link className="text-muted" to={paths.fr}>
                <img width="32" height="24" src="/image/france_square_icon.svg" alt="FR" style={{ display: 'block' }} />
              </Link>
            </div>
          </div>
        </header>

        <div className="container">
          {children}
        </div>
      </div>

      <footer className="blog-footer">
        <p></p>
      </footer>
    </>
  );
}
